<script setup>
import { ref, onMounted, onUnmounted, watchEffect } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Footer from '@/Components/Front/Footer.vue';

const props = defineProps({
    setting: Object,
    title: String,
    metaDescription: String,
});

const page = usePage();
const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);
const isDarkMode = ref(false);

const toggleTheme = () => {
    isDarkMode.value = !isDarkMode.value;
    if (isDarkMode.value) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('front_theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('front_theme', 'light');
    }
};

// Force document.title update on SPA navigation
watchEffect(() => {
    const newTitle = props.title || props.setting?.setting_title || 'Gotransport - Sewa Mobil Banyuwangi';
    if (typeof document !== 'undefined') {
        document.title = newTitle;
    }
});

const handleScroll = () => {
    isScrolled.value = window.scrollY > 50;
};

// Helper function to determine if a link is active
const isActive = (path) => {
    if (path === '/') {
        return page.url === '/';
    }
    return page.url.startsWith(path);
};

// Common classes
const baseClass = "px-4 py-2 rounded font-medium transition-all duration-200 inline-block";
const activeClass = "text-orange-700 bg-orange-50 dark:bg-gray-800 dark:text-orange-400";
const inactiveClass = "text-gray-900 hover:text-orange-700 hover:bg-orange-50 dark:text-gray-300 dark:hover:text-orange-400 dark:hover:bg-gray-800";

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
    
    // Check for saved theme preference
    if (localStorage.front_theme === 'dark') {
        isDarkMode.value = true;
        document.documentElement.classList.add('dark');
    } else {
        isDarkMode.value = false;
        document.documentElement.classList.remove('dark');
    }
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

const waNumber = props.setting?.setting_no_wa || '6281234567890';
const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent('Halo, saya ingin menyewa mobil')}`;
</script>

<template>
    <!-- Title is handled by: server-side Blade (SEO) + watchEffect (SPA navigation) -->
    <Head>
        <meta name="description" :content="metaDescription || 'Sewa mobil Banyuwangi murah dan terpercaya'" />
    </Head>

    <div class="font-sans antialiased bg-white dark:bg-gray-900 dark:text-gray-100 min-h-screen flex flex-col transition-colors duration-300">
        
        <!-- Header Section (Standard) -->
        <section id="header" class="sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-md transition-colors duration-300">
            <nav class="bg-white dark:bg-gray-900 transition-colors duration-300" id="navbar_sticky">
                <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl flex items-center justify-between h-20">
                    <!-- Logo -->
                    <Link class="py-3 font-bold text-black inline-block" href="/">
                        <img 
                            v-if="setting?.setting_logo"
                            :src="`/storage/logo/${setting.setting_logo}`" 
                            :alt="setting?.setting_logo_alt || 'Gotransport'"
                            width="210"
                            height="70"
                            class="max-w-full h-auto"
                        />
                        <span v-else class="text-xl font-bold text-gray-900 dark:text-white">{{ setting?.setting_title || 'Gotransport' }}</span>
                    </Link>

                    <!-- Mobile Theme Toggle -->
                    <!-- Mobile Theme Toggle (Fixed next to WA) -->
                    <button @click="toggleTheme" class="fixed right-20 top-4 md:hidden bg-white dark:bg-gray-800 text-gray-800 dark:text-yellow-400 w-12 h-12 rounded-full flex items-center justify-center shadow-lg border border-gray-100 dark:border-gray-700 z-50 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" aria-label="Toggle Theme">
                         <i class="fa text-xl" :class="isDarkMode ? 'fa-sun-o' : 'fa-moon-o'"></i>
                    </button>

                    <!-- Mobile Fixed WhatsApp Button -->
                    <a
                        :href="waLink"
                        class="fixed right-4 top-4 md:hidden bg-green-700 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-green-800 transition-colors z-50"
                        target="_blank"
                        rel="nofollow noopener"
                        aria-label="Hubungi kami via WhatsApp"
                    >
                        <i class="fa fa-whatsapp text-xl" aria-hidden="true"></i>
                    </a>

                    <!-- Desktop Menu -->
                    <div class="hidden md:block" id="navbarSupportedContent">
                        <ul class="flex items-center space-x-1 mb-0">
                            <!-- Theme Toggle Desktop -->
                            <li class="mr-2">
                                <button @click="toggleTheme" class="px-3 py-2 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-yellow-400 font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 inline-flex items-center justify-center w-10 h-10" aria-label="Toggle Dark Mode">
                                    <i class="fa" :class="isDarkMode ? 'fa-sun-o' : 'fa-moon-o'" aria-hidden="true"></i>
                                </button>
                            </li>
                            <li><Link :class="[baseClass, isActive('/') ? activeClass : inactiveClass]" href="/">Beranda</Link></li>
                            <li><Link :class="[baseClass, isActive('/layanan') ? activeClass : inactiveClass]" href="/layanan">Layanan</Link></li>
                            <li><Link :class="[baseClass, isActive('/daftar-armada') ? activeClass : inactiveClass]" href="/daftar-armada">Armada</Link></li>
                            <li><Link :class="[baseClass, isActive('/ulasan') ? activeClass : inactiveClass]" href="/ulasan">Ulasan</Link></li>
                            <li><Link :class="[baseClass, isActive('/berita-dan-artikel') ? activeClass : inactiveClass]" href="/berita-dan-artikel">Blog</Link></li>
                            <li>
                                <a class="px-3 py-2 rounded bg-blue-700 text-white font-bold hover:bg-blue-800 transition-all duration-200 inline-flex items-center justify-center w-10 h-10" :href="`tel:+62${(setting?.setting_no_telp || '').replace(/^0/, '')}`" aria-label="Hubungi Telepon Kami">
                                    <i class="fa fa-headphones" aria-hidden="true"></i>
                                </a>
                            </li>
                            <li>
                                <a class="px-3 py-2 rounded bg-green-700 text-white font-bold hover:bg-green-800 transition-all duration-200 inline-flex items-center justify-center w-10 h-10" :href="waLink" target="_blank" aria-label="Hubungi WhatsApp Kami">
                                    <i class="fa fa-whatsapp text-lg" aria-hidden="true"></i>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </section>

        <!-- Mobile Bottom Tab Bar -->
        <nav class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 z-40 md:hidden shadow-lg transition-colors duration-300">
            <div class="flex justify-around items-center h-14">
                <Link href="/" :class="['flex-1 flex flex-col items-center justify-center py-1.5 transition-all', isActive('/') ? 'text-orange-600 bg-orange-50 dark:bg-gray-800' : 'hover:text-orange-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-400']">
                    <span class="text-lg mb-0.5">🏠</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-gray-300">Beranda</span>
                </Link>
                <Link href="/layanan" :class="['flex-1 flex flex-col items-center justify-center py-1.5 transition-all', isActive('/layanan') ? 'text-orange-600 bg-orange-50 dark:bg-gray-800' : 'hover:text-orange-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-400']">
                    <span class="text-lg mb-0.5">⚙️</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-gray-300">Layanan</span>
                </Link>
                <Link href="/daftar-armada" :class="['flex-1 flex flex-col items-center justify-center py-1.5 transition-all', isActive('/daftar-armada') ? 'text-orange-600 bg-orange-50 dark:bg-gray-800' : 'hover:text-orange-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-400']">
                    <span class="text-lg mb-0.5">🚗</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-gray-300">Armada</span>
                </Link>
                <Link href="/ulasan" :class="['flex-1 flex flex-col items-center justify-center py-1.5 transition-all', isActive('/ulasan') ? 'text-orange-600 bg-orange-50 dark:bg-gray-800' : 'hover:text-orange-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-400']">
                    <span class="text-lg mb-0.5">⭐</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-gray-300">Ulasan</span>
                </Link>
                <Link href="/berita-dan-artikel" :class="['flex-1 flex flex-col items-center justify-center py-1.5 transition-all', isActive('/berita-dan-artikel') || isActive('/artikel') ? 'text-orange-600 bg-orange-50 dark:bg-gray-800' : 'hover:text-orange-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-400']">
                    <span class="text-lg mb-0.5">📝</span>
                    <span class="text-xs font-bold text-gray-900 dark:text-gray-300">Blog</span>
                </Link>
            </div>
        </nav>

        <!-- Main Content -->
        <main class="flex-grow">
            <slot />
        </main>

        <!-- Footer (Standard) -->
        <!-- Footer Component -->
        <Footer :setting="setting" />
    </div>
</template>

<style scoped>
.animate-bounce {
    animation: bounce 2s infinite;
}

@keyframes bounce {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10px);
    }
}
</style>
